import { useState } from 'react';
import { Link } from 'react-router';
import cn from 'classnames';
import type { Sponsor } from '@repo/types';
import { SponsorDescriptionDisplay, SponsorsGridFormData } from './contracts';
import { Image, ImageVariant } from '../../../common/components/Image';
import { useFilteredSponsors } from '../../hooks/useFilteredSponsors';
import { SkeletonLoader } from '@ui/common/components/SkeletonLoader';
import { readableTextColor } from '@ui/utils/readableTextColor';

export type SponsorsGridProps = SponsorsGridFormData & {
  className?: string;
};

/**
 * Resolves the effective description mode: the new tri-state option wins, and
 * configs saved before it existed fall back to the legacy boolean.
 */
export function resolveDescriptionDisplay(
  descriptionDisplay: SponsorDescriptionDisplay | undefined,
  showDescription: boolean = false
): SponsorDescriptionDisplay {
  if (descriptionDisplay) {
    return descriptionDisplay;
  }
  return showDescription ? SponsorDescriptionDisplay.Static : SponsorDescriptionDisplay.Hidden;
}

export function SponsorsGrid({
  title,
  showImage,
  showName,
  showDescription,
  descriptionDisplay,
  showBadges,
  showAllSponsors,
  sponsorIds,
  columns,
  className,
}: SponsorsGridProps) {
  const { sponsors, isLoading, error } = useFilteredSponsors({ showAllSponsors, sponsorIds });

  if (isLoading) {
    return (
      <div style={{ display: 'block', width: '100%', height: '100%' }}>
        <SkeletonLoader count={21} />
      </div>
    );
  }

  if (!sponsors || error) {
    return null;
  }

  const gridStyle = columns ? { gridTemplateColumns: `repeat(${columns}, 1fr)` } : undefined;
  const descriptionMode = resolveDescriptionDisplay(descriptionDisplay, showDescription);

  return (
    <div className={cn(className, 'sponsors-grid')}>
      {title && <h3 className={'sponsors-grid__title'}>{title}</h3>}
      <ul className={'sponsors-grid__sponsor-list'} style={gridStyle}>
        {sponsors.map((sponsor) => (
          <SponsorsGridItem
            key={sponsor.id}
            sponsor={sponsor}
            showImage={showImage}
            showName={showName}
            showBadge={showBadges !== false}
            descriptionMode={descriptionMode}
          />
        ))}
      </ul>
    </div>
  );
}

type SponsorsGridItemProps = {
  sponsor: Sponsor;
  showImage: boolean;
  showName: boolean;
  showBadge: boolean;
  descriptionMode: SponsorDescriptionDisplay;
};

function SponsorsGridItem({ sponsor, showImage, showName, showBadge, descriptionMode }: SponsorsGridItemProps) {
  const hasBadge = showBadge && !!sponsor.badgeText;
  const hasDescription = descriptionMode !== SponsorDescriptionDisplay.Hidden && !!sponsor.description;
  const hasBody = showName || hasBadge || hasDescription;

  const logoImage =
    showImage && sponsor.logo ? (
      <Image
        variant={ImageVariant.SponsorLogoXs}
        src={sponsor.logo}
        alt={sponsor.name}
        className={'sponsors-grid__sponsor-logo'}
        sizes="(max-width: 767px) calc(50vw - 48px), 228px"
      />
    ) : null;

  return (
    <li className={'sponsors-grid__sponsor-item'}>
      <div className={'sponsors-grid__sponsor-logo-area'}>
        {sponsor.url ? (
          <Link to={sponsor.url} className={'sponsors-grid__sponsor-link'} target="_blank" aria-label={sponsor.name}>
            {logoImage}
          </Link>
        ) : (
          logoImage
        )}
      </div>
      {hasBody && (
        <div className={'sponsors-grid__sponsor-body'}>
          {showName && <h3 className={'sponsors-grid__sponsor-name'}>{sponsor.name}</h3>}
          {hasBadge && <SponsorBadge badgeText={sponsor.badgeText!} badgeColor={sponsor.badgeColor} />}
          {hasDescription && <SponsorDescription description={sponsor.description} mode={descriptionMode} />}
        </div>
      )}
    </li>
  );
}

function SponsorDescription({ description, mode }: { description: string; mode: SponsorDescriptionDisplay }) {
  const [isExpanded, setIsExpanded] = useState(false);

  if (mode === SponsorDescriptionDisplay.Static) {
    return <p className={'sponsors-grid__sponsor-description text-3xs'}>{description}</p>;
  }

  return (
    <>
      <button
        type="button"
        className={'sponsors-grid__sponsor-toggle text-3xs'}
        aria-expanded={isExpanded}
        onClick={() => setIsExpanded((prev) => !prev)}
      >
        <span>{isExpanded ? 'Show less' : 'Learn more'}</span>
        <ToggleChevronIcon />
      </button>
      <p
        className={'sponsors-grid__sponsor-description sponsors-grid__sponsor-description--expandable text-3xs'}
        aria-hidden={!isExpanded}
      >
        {description}
      </p>
    </>
  );
}

function SponsorBadge({ badgeText, badgeColor }: { badgeText: string; badgeColor?: string }) {
  // The colour is admin-chosen, so the chip picks a readable text colour at
  // runtime; without a colour the chip keeps its neutral SCSS styling.
  const style = badgeColor
    ? { backgroundColor: badgeColor, borderColor: badgeColor, color: readableTextColor(badgeColor) }
    : undefined;

  return (
    <span className={'sponsors-grid__sponsor-badge text-3xs'} style={style}>
      {badgeText}
    </span>
  );
}

function ToggleChevronIcon() {
  return (
    <svg width="10" height="6" viewBox="0 0 10 6" fill="none" aria-hidden="true">
      <path d="M1 1l4 4 4-4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
